import React from 'react';
import { connect } from 'react-redux';
import {
  SystemTrustedCertificatesTable,
  refetchTable,
  deleteTrustedCertificate,
  showToast,
} from '@kineticdata/react';
import { generateEmptyBodyRow, Icon } from '@kineticdata/ui';
import { action, dispatch, regHandlers } from '../../store';
import { ControlGroup } from './ControlGroup';
import { CertificateDetailsModal } from '@kineticdata/ui/src';
import { Link } from 'react-router-dom';
import { restartResourcesToastDefinition } from '../system/SystemSetupTrustedCertificatesNew';
import { reload } from './NoticesDropdown';

regHandlers({
  OPEN_TRUSTED_CERTIFICATE_MODAL: (state, { payload }) =>
    state.setIn(['page', 'trustedCertificates', 'viewCertificate'], payload),
  CLOSE_TRUSTED_CERTIFICATE_MODAL: state =>
    state.deleteIn(['page', 'trustedCertificates', 'viewCertificate']),
});

const openModal = certificate => () =>
  dispatch(action('OPEN_TRUSTED_CERTIFICATE_MODAL', certificate));
const closeModal = () => dispatch(action('CLOSE_TRUSTED_CERTIFICATE_MODAL'));
const handleRefresh = tableKey => () => refetchTable(tableKey);

const EmptyBodyRow = generateEmptyBodyRow({
  loadingMessage: 'Loading Trusted Certificates...',
  noSearchResultsMessage:
    'No Trusted Certificates were found - please modify your search criteria',
  noItemsMessage: 'There are no Trusted Certificates to display.',
});

const LinkingCell = ({ value, row }) => (
  <td>
    <button
      type="button"
      className="btn btn-text"
      onClick={openModal(row)}
      title={value}
    >
      {value || <em>Certificate Details</em>}
    </button>
  </td>
);

const ActionsCell = ({ row, tableKey, tableOptions: { spaceSlug } }) => (
  <td>
    <ControlGroup
      controlKey={row.getIn(['fingerprints', 'sha1'])}
      actions={{
        delete: fingerprint => {
          showToast(restartResourcesToastDefinition);
          reload();
          return deleteTrustedCertificate({ fingerprint, spaceSlug });
        },
      }}
      confirmations={{
        delete: {
          title: 'Are you sure you want to delete this row?',
          body: 'Deleting this is a permanent action. Are you sure you want to continue?',
          actionName: 'Delete Trusted Certificate',
          actionType: 'danger',
        },
      }}
      onSuccess={() => {
        refetchTable(tableKey);
      }}
    >
      {({ actions, anyPending, pending }) => (
        <div className="btn-set">
          <button
            className="btn btn-table"
            type="button"
            disabled={anyPending}
            onClick={actions.delete(row.getIn(['fingerprints', 'sha1']))}
            aria-label="Delete"
            title="Delete"
          >
            {pending.get('delete') ? (
              <Icon name="circle-notch" md spin />
            ) : (
              <Icon name="trash" md />
            )}
          </button>
        </div>
      )}
    </ControlGroup>
  </td>
);

const mapStateToProps = state => ({
  viewCertificate: state.getIn([
    'page',
    'trustedCertificates',
    'viewCertificate',
  ]),
  notifications: state.getIn(['page', 'notifications']),
});

export const TrustedCertificatesList = connect(mapStateToProps)(({
  viewCertificate,
  tableKey,
  spaceSlug,
}) => {
  return (
    <SystemTrustedCertificatesTable
      tableKey={tableKey}
      spaceSlug={spaceSlug}
      components={{ EmptyBodyRow }}
      columnSet={['subjectName', 'expires', 'issuerName', '_actions']}
      alterColumns={{
        subjectName: {
          components: { BodyCell: LinkingCell },
        },
      }}
      addColumns={[
        {
          value: '_actions',
          title: '',
          sortable: false,
          components: { BodyCell: ActionsCell },
        },
      ]}
    >
      {({ table, pagination, filter, filterControl }) => (
        <>
          <div className="heading heading-sm">
            <div className="btn-set btn-set-sm ms-auto">
              {filterControl}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleRefresh(tableKey)}
                aria-label="Refresh"
                title="Refresh"
              >
                <Icon name="refresh-cw" />
              </button>
              <Link
                className="btn btn-outline-primary btn-sm"
                to={
                  !spaceSlug
                    ? '/system/setup/infra/certs/new'
                    : `/spaces/edit/${spaceSlug}/certs/new`
                }
              >
                <Icon name="plus" md />
                <span>Add Certificates</span>
              </Link>
            </div>
          </div>

          <div className="content-container">
            {filter}
            {table}
            {pagination}
          </div>

          {viewCertificate && (
            <CertificateDetailsModal
              certificate={viewCertificate}
              isOpen={true}
              toggle={closeModal}
              title="Trusted Certificate Details"
              downloadFilename={`certificate-${viewCertificate
                .getIn(['fingerprints', 'sha1'])
                .replaceAll(/\s/g, '')}.pem`}
            />
          )}
        </>
      )}
    </SystemTrustedCertificatesTable>
  );
});
